import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type {
  BespokeTemplateCreate,
  BespokeTemplateResponse,
  BespokeTemplateUpdate,
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { isSectionEditorDraftDirty } from '../../store/slices/formSchemaEditorSlice';
import type { SectionEditorDraft } from '../../store/slices/formSchemaEditorSlice';
import { FormSchemaSectionNav } from './FormSchemaSectionNav';
import { SectionEditorPanel } from './TemplateVibecoder';
import type { PreviewViewport } from './previewViewport';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import type {
  FormTree,
  FormTreeQuestion,
  SectionGenerationStatus,
  SectionLayoutState,
} from './formSchemaTypes';
import type { EditorTarget } from './editorTarget';
import type { CustomGenerateTarget } from './CustomGenerateDialog';
import {
  formatRemotePushTimeLabel,
  getSectionLayoutState,
  latestUnacknowledgedRemotePush,
  layoutGenerationKey,
  sectionEditorDraftKey,
  type RemotePushByTemplateId,
} from './formSchemaUtils';
import type { SectionConsistency } from './sectionConsistency';

export function FormSchemaStudio({
  selectedRogerDocFormType,
  selectedVersion,
  selectedSectionName,
  formTree,
  formTreeLabelByCategory,
  orderedFormTreeSections,
  treeLoading,
  formTypeCatalog,
  catalogLoading,
  editorTarget,
  editorMatchesSection,
  selectedSectionQuestions,
  enabledTemplateBySection,
  templatesBySection,
  editorDraftByKey,
  sectionGenerationStatusByKey,
  remotePushByTemplateId,
  saving,
  getSectionConsistency,
  onSelectSection,
  onSaveJson,
  onGenerate,
  onGenerateCustom,
  onAddFromClipboard,
  onCopyLayout,
  onViewQuestions,
  onOpenVersions,
  previewViewport,
  onPreviewViewportChange,
}: {
  selectedRogerDocFormType: RogerDocFormType | null;
  selectedVersion: FormSchemaVersion;
  selectedSectionName: string | null;
  formTree: FormTree | null;
  formTreeLabelByCategory: Record<string, string>;
  orderedFormTreeSections: string[];
  treeLoading: boolean;
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  catalogLoading: boolean;
  editorTarget: EditorTarget | null;
  editorMatchesSection: boolean;
  selectedSectionQuestions: FormTreeQuestion[] | null;
  enabledTemplateBySection: Map<string, BespokeTemplateResponse>;
  templatesBySection: Map<string, BespokeTemplateResponse[]>;
  editorDraftByKey: Record<string, SectionEditorDraft | undefined>;
  sectionGenerationStatusByKey: Record<
    string,
    SectionGenerationStatus | undefined
  >;
  remotePushByTemplateId: RemotePushByTemplateId;
  saving: boolean;
  getSectionConsistency: (section: string) => SectionConsistency;
  onSelectSection: (sectionName: string) => void;
  onSaveJson: (
    data: BespokeTemplateCreate | BespokeTemplateUpdate
  ) => void | Promise<void>;
  onGenerate: (section: string, questions: FormTreeQuestion[]) => void;
  onGenerateCustom: (target: CustomGenerateTarget) => void;
  onAddFromClipboard: (section: string, questions: FormTreeQuestion[]) => void;
  onCopyLayout: (layoutState: SectionLayoutState) => void;
  onViewQuestions: (section: string, questions: FormTreeQuestion[]) => void;
  onOpenVersions: (section: string) => void;
  // Owned by the workspace so form-type switches (which clear selectedVersion
  // and remount this studio) do not reset the user's phone/tablet preview.
  previewViewport: PreviewViewport;
  onPreviewViewportChange: (viewport: PreviewViewport) => void;
}) {
  // Held here, not in SectionEditorPanel: that panel is keyed by section and
  // remounts on every section-tab change, which would reset the dialog.
  const [layoutJsonOpen, setLayoutJsonOpen] = useState(false);

  const selectSection = (sectionName: string) => {
    setLayoutJsonOpen(false);
    onSelectSection(sectionName);
  };

  if (treeLoading) {
    return (
      <div className='pt-4'>
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='h-5 w-5 animate-spin' />
        </div>
      </div>
    );
  }

  if (!formTree) {
    return (
      <div className='pt-4'>
        <p className='text-muted-foreground text-center py-8'>
          Failed to load form tree.
        </p>
      </div>
    );
  }

  if (orderedFormTreeSections.length === 0) {
    return (
      <div className='pt-4'>
        <p className='text-muted-foreground text-center py-8'>
          This schema version has no sections.
        </p>
      </div>
    );
  }

  return (
    <div className='pt-4'>
      <div className='flex min-h-[32rem] gap-3'>
        <FormSchemaSectionNav
          sections={orderedFormTreeSections}
          labelFor={section =>
            formatSectionDropdownLabel(section, formTreeLabelByCategory)
          }
          selectedSection={selectedSectionName}
          onSelect={selectSection}
          getLayoutState={section =>
            getSectionLayoutState(
              enabledTemplateBySection.get(section),
              formTree[section] ?? []
            )
          }
          getConsistency={getSectionConsistency}
          getGenerationStatus={section =>
            sectionGenerationStatusByKey[
              layoutGenerationKey(selectedVersion.name, section)
            ]
          }
          getChatPending={section =>
            selectedRogerDocFormType
              ? (editorDraftByKey[
                  sectionEditorDraftKey(
                    selectedRogerDocFormType,
                    selectedVersion.name,
                    section
                  )
                ]?.chatPending ?? false)
              : false
          }
          getIsDirty={section =>
            selectedRogerDocFormType
              ? isSectionEditorDraftDirty(
                  editorDraftByKey[
                    sectionEditorDraftKey(
                      selectedRogerDocFormType,
                      selectedVersion.name,
                      section
                    )
                  ]
                )
              : false
          }
          getHasRemotePush={section =>
            latestUnacknowledgedRemotePush(
              remotePushByTemplateId,
              (templatesBySection.get(section) ?? []).map(t => t.id)
            ) != null
          }
          getRemotePushTooltip={section => {
            const latest = latestUnacknowledgedRemotePush(
              remotePushByTemplateId,
              (templatesBySection.get(section) ?? []).map(t => t.id)
            );
            return latest
              ? `New layout pushed · ${formatRemotePushTimeLabel(latest.updatedAt)}`
              : undefined;
          }}
          onGenerate={section => onGenerate(section, formTree[section] ?? [])}
          onGenerateCustom={section => {
            onGenerateCustom({
              sectionName: section,
              displayName: formatSectionDropdownLabel(
                section,
                formTreeLabelByCategory
              ),
              questions: formTree[section] ?? [],
            });
          }}
          onAddFromClipboard={section =>
            onAddFromClipboard(section, formTree[section] ?? [])
          }
          onCopyLayout={section =>
            onCopyLayout(
              getSectionLayoutState(
                enabledTemplateBySection.get(section),
                formTree[section] ?? []
              )
            )
          }
          onViewQuestions={section =>
            onViewQuestions(section, formTree[section] ?? [])
          }
          onOpenVersions={section => {
            selectSection(section);
            onOpenVersions(section);
          }}
          onOpenLayoutJson={section => {
            selectSection(section);
            setLayoutJsonOpen(true);
          }}
        />
        <div className='min-w-0 flex-1 pl-2'>
          {selectedSectionName &&
          selectedSectionQuestions &&
          editorTarget &&
          editorMatchesSection ? (
            <SectionEditorPanel
              key={`${selectedVersion.name}::${selectedSectionName}`}
              editingTemplate={editorTarget.editingTemplate}
              initialDraft={editorTarget.draft}
              onSaveJson={onSaveJson}
              saving={saving}
              formTypeCatalog={formTypeCatalog}
              catalogLoading={catalogLoading}
              selectedRogerDocFormType={selectedRogerDocFormType}
              activeTemplateId={
                selectedSectionName
                  ? (enabledTemplateBySection.get(selectedSectionName)?.id ??
                    null)
                  : null
              }
              remotePush={
                editorTarget.editingTemplate
                  ? (remotePushByTemplateId[editorTarget.editingTemplate.id] ??
                    null)
                  : null
              }
              layoutJsonOpen={layoutJsonOpen}
              onLayoutJsonOpenChange={setLayoutJsonOpen}
              previewViewport={previewViewport}
              onPreviewViewportChange={onPreviewViewportChange}
            />
          ) : (
            <div className='flex min-h-[24rem] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground'>
              Select a section to edit its layout.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
